import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ForgotPasswordForm from 'services/Auth/components/ForgotPasswordForm'
import ModalTimeout from 'services/Auth/components/ModalTimeout'
import NewPasswordForm from 'services/Auth/components/NewPasswordForm'
import OtpFlowModal from 'services/Auth/components/OtpFlowModal'
import SuccessResetPassword from 'services/Auth/components/SuccessResetPassword'
import ModalOtaInvalid from 'services/Auth/widgets/ModalOtaInvalid'
import { useStoreActions, useStoreState } from 'stores/hooks'

export default function ResetPasswordFlow({
  responsive,
}: {
  responsive?: boolean
}): JSX.Element {
  const [password, setPassword] = useState('')
  const [isTimeout, setIsTimeout] = useState(false)

  const { t } = useTranslation()
  const {
    step,
    successResetPassword,
    isLoading,
    otaInvalid,
    errorMessage,
  } = useStoreState((states) => states.forgotPassword)

  const { otpTimer } = useStoreState((states) => states.otp)

  const {
    postResetPassword,
    setStep,
    postVerificationNumber,
    postOneTimePassword,
  } = useStoreActions((actions) => actions.forgotPassword)

  const isOpenModal = useMemo(() => {
    return ['otp-channel', 'otp-form'].includes(step)
  }, [step])

  const onSubmitResetPassword = useCallback(() => {
    postResetPassword(password)
  }, [password, postResetPassword])

  const onReload = useCallback(() => window.location.reload(), [])

  const handleClose = useCallback(() => {
    if (step === 'otp-form') return
    setStep('forgot-password-form')
  }, [setStep, step])

  const handleTimeout = useCallback(() => {
    setIsTimeout(true)
  }, [])

  useEffect(() => {
    setStep('forgot-password-form')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <>
      {step === 'new-password-form' ? (
        <NewPasswordForm
          onSubmit={onSubmitResetPassword}
          setTimeout={setTimeout}
          password={password}
          setPassword={setPassword}
          responsive={responsive}
        />
      ) : (
        <ForgotPasswordForm responsive={responsive} />
      )}

      <ModalTimeout
        isOpen={isTimeout}
        textButton={t('RETRY_FORGOT_PASSWORD', { ns: 'auth' })}
        timeoutText={t('RETRY_FORGOT_PASSWORD_DESC', { ns: 'auth' })}
        timeoutTitle={t('TIMEOUT', { ns: 'auth' })}
        fnButton={onReload}
      />

      <SuccessResetPassword
        isOpen={successResetPassword}
        enableTranslation
      />

      <OtpFlowModal
        isOpen={isOpenModal && !isTimeout}
        isLoading={isLoading}
        step={step}
        otpTimer={otpTimer}
        otpType="OTA"
        responsive={responsive}
        handleClose={handleClose}
        handleSetStep={setStep}
        handleTimeoutOtp={handleTimeout}
        handlePostSendOtp={postVerificationNumber}
        handlePostVerifyOtp={postOneTimePassword}
      />
      <ModalOtaInvalid
        isOpen={otaInvalid}
        errMessage={errorMessage}
        textButton={t('RETRY_FORGOT_PASSWORD', { ns: 'auth' })}
        title={t('ERROR_OCCURS', { ns: 'common' })}
      />
    </>
  )
}
